import { find, orderBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import { TId } from '@/features/common';
import { usePreferenceMutation, usePreferencesQuery } from '@/features/me';
import { useNotifications } from '@/features/notifications';
import { usePipelineCategoriesQuery } from '@/features/pipeline';

import { TestType } from '@/features/test';
import {
  useEmailTemplateVariablesQuery,
  useEmailTemplatesQuery,
} from '../queries';
import { IEmailTemplate, TEmailTemplateType } from '../types';
import { convertVariablesFromCamelCase, isSystemTemplate } from '../utils';
import { useEmailModalContext } from './email-modal-context';

export const useEmailTemplates = () => {
  const { data: emailTemplates } = useEmailTemplatesQuery();

  const sortedEmailTemplates = emailTemplates?.length
    ? orderBy(
        emailTemplates.filter((template) => template.status === 'active'),
        [
          (template) => {
            if (isSystemTemplate(template)) return 1; // system
            return 2; // others
          },
          (template) => template.name.toLowerCase(),
        ],
        ['asc']
      )
    : [];

  return sortedEmailTemplates;
};

export const useSelectedEmailTemplatesByTypeAndTestId = (
  testId: TId | undefined,
  templateType: TEmailTemplateType | undefined,
  jobOpeningId?: TId | null,
  interviewId?: TId | null
) => {
  const emailModalContext = useEmailModalContext();
  const { data: emailTemplates } = useEmailTemplatesQuery();
  const { data: userPreferences } = usePreferencesQuery();
  const updateUserPreference = usePreferenceMutation();
  const pipelineCategoriesQuery = usePipelineCategoriesQuery(
    { jobOpeningId: jobOpeningId! },
    {
      enabled: !!jobOpeningId,
    }
  );
  const pipelineCategoryWithTest = useMemo(
    () => pipelineCategoriesQuery.data?.find((item) => item.testId === testId),
    [pipelineCategoriesQuery, testId]
  );

  /*
System templates Id's:
1 - Test invitation
2 - Rejection without feedback
3 - Test reminder
4 - Async interview invitation
5 - Feedback Template
13 - Interview invitation
We need to hardcode these values because we removed separate template types for each type of email.
*/
  const defaultSystemTempalteByEmailModalType =
    useMemo((): IEmailTemplate | null => {
      if (!emailModalContext?.emailModal?.type) return null;
      let emailTemplateId: string;

      // Ugly workaround because there is no separate email modal type for interview invitation.
      if (
        ['gmail-candidate', 'contact-candidate'].includes(
          emailModalContext?.emailModal?.type
        ) &&
        interviewId
      ) {
        return findTemplateById(emailTemplates, '13'); //Interview invitation
      }

      const defaultInvitationTempalteIdByTestType: Partial<
        Record<TestType, TId>
      > = {
        quiz: '1', // Test invitation template
        homework: '1', // Test invitation template
        video: '4', // Async interview invitation
      };

      switch (emailModalContext?.emailModal?.type) {
        case 'contact-candidate':
        case 'gmail-candidate':
        case 'gmail-bulk-contact-candidate':
        case 'bulk-contact-candidate':
          return null; // No default system template for these types
        case 'test-invitation':
        case 'next-stage-invitation':
          emailTemplateId =
            defaultInvitationTempalteIdByTestType[
              pipelineCategoryWithTest?.testType || 'quiz'
            ] || '1';
          break;
        case 'resend-next-stage-invitation':
        case 'resend-invitation':
        case 'resend-test-invitation':
          emailTemplateId = '3'; // Test reminder
          break;
        case 'rejection':
        case 'bulk-rejection':
          emailTemplateId = '2'; // Rejection without feedback
          break;
      }

      return findTemplateById(emailTemplates, emailTemplateId);
    }, [
      emailModalContext,
      emailTemplates,
      interviewId,
      pipelineCategoryWithTest,
    ]);

  /* 
  Get last used by the user email template for the given test and template type, select system template if user has not selected any.
  In cases where we don't have a test set yet per job, we will be saving them as ${templateType}_noTest
  For 'contact-candidate' (and 'gmail-candidate') by default we don't select any system template. 
  */
  const currentEmailTemplate: IEmailTemplate | null = useMemo(
    () =>
      (userPreferences &&
        find(emailTemplates, {
          id: userPreferences.selectedEmailTemplatesByTypeAndTestId[
            pipelineCategoryWithTest
              ? `${templateType}_${testId}`
              : `${templateType}`
          ]!,
        })) ||
      defaultSystemTempalteByEmailModalType,
    [
      userPreferences,
      emailTemplates,
      templateType,
      testId,
      pipelineCategoryWithTest,
      defaultSystemTempalteByEmailModalType,
    ]
  );

  const setCurrentEmailTemplate = useCallback(
    (emailTemplateId: TId | null) => {
      userPreferences?.selectedEmailTemplatesByTypeAndTestId &&
        updateUserPreference.mutate({
          name: 'selectedEmailTemplatesByTypeAndTestId',
          value: {
            ...userPreferences.selectedEmailTemplatesByTypeAndTestId,
            [pipelineCategoryWithTest
              ? `${templateType}_${testId}`
              : `${templateType}`]: emailTemplateId,
          },
        });
    },
    [
      updateUserPreference,
      userPreferences?.selectedEmailTemplatesByTypeAndTestId,
      templateType,
      testId,
      pipelineCategoryWithTest,
    ]
  );
  return {
    setCurrentEmailTemplate,
    currentEmailTemplate,
  };
};

export const useEmailTemplateVariables = () => {
  const { addNotification } = useNotifications();

  const { data: emailTemplateVariables } = useEmailTemplateVariablesQuery();

  const emailTemplateVariablesUsedAreValid = useCallback(
    (textWithVariables: string) => {
      const variableRegex = /\[.*?\]/g;
      const usedVariables = textWithVariables.match(variableRegex);

      const invalidVariable = usedVariables?.find(
        (usedVariable) =>
          !emailTemplateVariables?.find(
            (variable) => `[${variable.placeholder}]` === usedVariable
          )
      );

      if (invalidVariable) {
        addNotification({
          type: 'email_template_variable_invalid',
          meta: { variable: convertVariablesFromCamelCase(invalidVariable) },
        });
        return false;
      }
      return true;
    },
    [emailTemplateVariables, addNotification]
  );

  return {
    emailTemplateVariables: emailTemplateVariables || [],
    emailTemplateVariablesUsedAreValid,
  };
};

const findTemplateById = (
  templates: IEmailTemplate[] | undefined,
  id: string
): IEmailTemplate | null => {
  return templates?.find((template) => template.id === id) || null;
};
